import React from 'react';
import './DiscoveryCards.css';
import RecoverableImage from '../images/RecoverableImage';
import AlbumContentTitle from '../../components/content/AlbumContentTitle';
import { SponsoredAlbum } from '../ads/SponsoredContent';
import { sponsoredRail } from '../ads/placement';
import ContentShelf from '../../components/responsive/ContentShelf';
import { Link } from 'react-router-dom';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { albumPath } from '../../components/content/DiscoveryContent';
import HydratedPlaylistArtwork from '../../components/content/HydratedPlaylistArtwork';
import ProfileIdentity from '../../components/content/ProfileIdentity';
import { discoveryReason } from './presentation';
import {
  RecommendationSlot,
  recommendationEvent,
  useRecommendationTracking,
} from './RecommendationTracking';
import RecommendationFeedback from './RecommendationFeedback';
export function DiscoveryAlbums({ items, data, surface, onClick, campaigns = [] }) {
  const { t, locale } = useWebLocale();
  const { record } = useRecommendationTracking();
  const Wrapper = surface === 'HOME' ? ContentShelf : 'div';
  return (
    <Wrapper
      label={surface === 'HOME' ? t('home.newDiscovery') : undefined}
      className={surface === 'HOME' ? '' : 'pp-album-grid pp-search-albums'}
    >
      {sponsoredRail(items, campaigns, surface === 'HOME' ? 12 : items.length).map((entry) => {
        if (entry.kind === 'sponsored')
          return (
            <SponsoredAlbum
              key={`ad-${entry.campaign.id}-${entry.occurrence}`}
              campaign={entry.campaign}
              placementKey="HOME_DISCOVERY"
              reasonChip={surface === 'HOME'}
            />
          );
        const { item, position } = entry;
        const event = recommendationEvent(
          data,
          surface,
          surface === 'HOME' ? 'NEW_DISCOVERY' : 'ALBUMS',
          item,
          position,
        );
        const path = albumPath(item) + (item.openReview ? `/user-reviews/${item.reviewId}` : '');
        return (
          <RecommendationSlot key={item.id} event={event} className="pp-discovery-album">
            <Link
              className="pp-album-card"
              to={path}
              onClick={() => {
                onClick?.(item.id);
                if (event)
                  record([
                    { ...event, eventType: 'RECOMMENDATION_CLICK' },
                    { ...event, eventType: item.openReview ? 'REVIEW_OPEN' : 'ALBUM_OPEN' },
                  ]);
              }}
            >
              <div className="pp-artwork">
                <RecoverableImage
                  src={item.imageUrl || item.albumImageUrl || undefined}
                  alt=""
                  loading="lazy"
                />
              </div>
              <AlbumContentTitle item={item} title={item.title || item.albumTitle} />
              <span className="pp-secondary pp-content-artist">
                {item.fallback && item.homeReasonKey
                  ? (item.artist?.name ?? t('common.noArtist'))
                  : item.artist?.name || item.artistName || item.albumArtistName}
              </span>
              <span className="pp-discovery-reason">{discoveryReason(item, t, locale)}</span>
            </Link>
            {surface !== 'HOME' && event && <RecommendationFeedback item={item} event={event} />}
          </RecommendationSlot>
        );
      })}
    </Wrapper>
  );
}
export function DiscoveryReviews({ items, data }) {
  const { locale } = useWebLocale();
  const { record } = useRecommendationTracking();
  return (
    <div className="pp-review-grid">
      {items.map((item, index) => {
        const event =
          item.reason && recommendationEvent(data, 'SEARCH_HOME', 'REVIEWS', item, index + 1);
        return (
          <RecommendationSlot key={item.reviewId} event={event} className="pp-review-card">
            <Link
              aria-label={`${item.albumTitle} · ${item.userNickname} · ${locale === 'en' ? 'Review' : '리뷰'}`}
              to={`${albumPath(item)}/user-reviews/${item.reviewId}`}
              onClick={() => {
                if (event)
                  record([
                    { ...event, eventType: 'RECOMMENDATION_CLICK' },
                    { ...event, eventType: 'REVIEW_OPEN' },
                  ]);
              }}
            >
              <p>{item.content}</p>
              <div className="pp-review-meta">
                <RecoverableImage src={item.albumImageUrl || undefined} alt="" loading="lazy" />
                <div>
                  <AlbumContentTitle item={item} title={item.albumTitle} />
                  <span className="pp-secondary pp-content-artist">
                    {item.artist?.name || item.albumArtistName}
                  </span>
                  <ProfileIdentity
                    name={item.userNickname}
                    image={item.userProfileImageUrl}
                    mark={item.userProfileMark}
                  />
                  <span className="pp-rating">★ {Number(item.rating).toFixed(1)}</span>
                </div>
              </div>
            </Link>
          </RecommendationSlot>
        );
      })}
    </div>
  );
}
export function DiscoveryPlaylists({ items, data }) {
  const { t } = useWebLocale();
  const { record } = useRecommendationTracking();
  return (
    <div className="pp-feature-grid">
      {items.map((item, index) => {
        const event =
          item.reason && recommendationEvent(data, 'SEARCH_HOME', 'PLAYLISTS', item, index + 1);
        return (
          <RecommendationSlot key={item.id} event={event}>
            <Link
              className="pp-feature-card pp-playlist-feature"
              to={`/lists/${encodeURIComponent(item.slug || item.id)}`}
              onClick={() => {
                if (event)
                  record([
                    { ...event, eventType: 'RECOMMENDATION_CLICK' },
                    { ...event, eventType: 'PLAYLIST_OPEN' },
                  ]);
              }}
            >
              <HydratedPlaylistArtwork playlist={item} />
              <div>
                <h3>{item.title}</h3>
                <ProfileIdentity
                  name={item.username || item.userNickname}
                  image={item.userProfileImageUrl}
                  mark={item.userProfileMark}
                />
                <p className="pp-secondary">
                  {item.type === 'ALBUM'
                    ? `${t('albums')} ${item.albumCount}`
                    : `♪ ${item.trackCount}`}
                </p>
                {item.description && <p>{item.description}</p>}
              </div>
            </Link>
          </RecommendationSlot>
        );
      })}
    </div>
  );
}
